import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout';
import { Button, Input, Card } from '@/components/ui';
import { useCustomer, useCustomerPhones } from '../hooks';
import { CustomerPhoneList } from '../components';
import { PhoneInput } from '../components';
import { updateCustomer, deleteCustomer } from '../api/customersApi';
import { createDefaultPhoneFormItem, PHONE_TYPES } from '../types';
import type { PhoneFormItem, CustomerPhoneSummary, PhoneType } from '../types';

export function CustomerDetailPage() {
  const { customerId } = useParams<{ customerId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { customer, isLoading, error, refetch } = useCustomer({
    customerId: customerId || null,
  });

  const {
    addPhone,
    updatePhoneById,
    deletePhoneById,
    setAsPrimary,
    isAdding,
    isUpdating,
    isDeleting,
    error: phoneError,
  } = useCustomerPhones({
    customerId: customerId || '',
    onSuccess: refetch,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isAddingPhone, setIsAddingPhone] = useState(false);
  const [newPhone, setNewPhone] = useState<PhoneFormItem>(
    createDefaultPhoneFormItem(false)
  );

  // Phone editing state
  const [editingPhoneId, setEditingPhoneId] = useState<string | null>(null);
  const [editPhoneForm, setEditPhoneForm] = useState<{
    phoneNumber: string;
    phoneType: PhoneType;
    label: string;
    canSms: boolean;
    notes: string;
  } | null>(null);

  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    billing_street_address: '',
    billing_unit: '',
    billing_city: '',
    billing_province: '',
    billing_postal_code: '',
    billing_country: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Auto-start edit mode if ?edit=true in URL
  useEffect(() => {
    if (customer && searchParams.get('edit') === 'true' && !isEditing) {
      setEditForm({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email || '',
        billing_street_address: customer.billing_street_address || '',
        billing_unit: customer.billing_unit || '',
        billing_city: customer.billing_city || '',
        billing_province: customer.billing_province || '',
        billing_postal_code: customer.billing_postal_code || '',
        billing_country: customer.billing_country || '',
      });
      setIsEditing(true);
      // Clear the edit param from URL
      setSearchParams({}, { replace: true });
    }
  }, [customer, searchParams, isEditing, setSearchParams]);

  const handleStartEdit = () => {
    if (customer) {
      setEditForm({
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email || '',
        billing_street_address: customer.billing_street_address || '',
        billing_unit: customer.billing_unit || '',
        billing_city: customer.billing_city || '',
        billing_province: customer.billing_province || '',
        billing_postal_code: customer.billing_postal_code || '',
        billing_country: customer.billing_country || '',
      });
      setIsEditing(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!customerId) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await updateCustomer(customerId, editForm);
      setIsEditing(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update customer';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!customerId) return;
    if (
      !confirm(
        'Are you sure you want to delete this customer? This cannot be undone.'
      )
    ) {
      return;
    }

    try {
      await deleteCustomer(customerId);
      navigate('/customers');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to delete customer';
      setSaveError(message);
    }
  };

  const handleAddPhone = async () => {
    if (!newPhone.phoneNumber.trim()) return;

    const result = await addPhone({
      phone_number: newPhone.phoneNumber,
      phone_type: newPhone.phoneType,
      label: newPhone.label || undefined,
      is_primary: newPhone.isPrimary,
      can_sms: newPhone.canSms,
      notes: newPhone.notes || undefined,
    });

    if (result) {
      setIsAddingPhone(false);
      setNewPhone(createDefaultPhoneFormItem(false));
    }
  };

  const handleDeletePhone = async (phoneId: string) => {
    if (!confirm('Are you sure you want to delete this phone number?')) {
      return;
    }
    await deletePhoneById(phoneId);
  };

  const handleStartEditPhone = (phone: CustomerPhoneSummary) => {
    setEditingPhoneId(phone.id);
    setEditPhoneForm({
      phoneNumber: phone.phone_number,
      phoneType: phone.phone_type as PhoneType,
      label: phone.label || '',
      canSms: phone.can_sms,
      notes: phone.notes || '',
    });
  };

  const handleCancelEditPhone = () => {
    setEditingPhoneId(null);
    setEditPhoneForm(null);
  };

  const handleSaveEditPhone = async () => {
    if (!editingPhoneId || !editPhoneForm) return;

    const result = await updatePhoneById(editingPhoneId, {
      phone_number: editPhoneForm.phoneNumber,
      phone_type: editPhoneForm.phoneType,
      label: editPhoneForm.label || undefined,
      can_sms: editPhoneForm.canSms,
      notes: editPhoneForm.notes || undefined,
    });

    if (result) {
      setEditingPhoneId(null);
      setEditPhoneForm(null);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <svg
                className="animate-spin w-5 h-5 text-primary-600"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              <span className="text-secondary-600">Loading customer...</span>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !customer) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-secondary-900 mb-2">
              Customer not found
            </h2>
            <p className="text-secondary-500 mb-6">
              {error || 'The customer you are looking for does not exist.'}
            </p>
            <Link to="/customers">
              <Button variant="outline">Back to Customers</Button>
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/customers"
            className="p-2 text-secondary-400 hover:text-secondary-600
                       hover:bg-secondary-100 rounded-lg transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </Link>
          <h1 className="text-2xl font-bold text-secondary-900">
            {customer.full_name}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={handleStartEdit}>
                Edit
              </Button>
              {customer.project_count === 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-600 hover:bg-red-50"
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Error */}
      {(saveError || phoneError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">{saveError || phoneError}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Customer Info Card */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-secondary-900 mb-4">
              Contact Information
            </h2>

            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="First Name"
                    name="first_name"
                    value={editForm.first_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, first_name: e.target.value })
                    }
                    disabled={isSaving}
                    required
                  />
                  <Input
                    label="Last Name"
                    name="last_name"
                    value={editForm.last_name}
                    onChange={(e) =>
                      setEditForm({ ...editForm, last_name: e.target.value })
                    }
                    disabled={isSaving}
                    required
                  />
                </div>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                  disabled={isSaving}
                />

                <div className="pt-4 border-t border-secondary-200">
                  <h3 className="text-sm font-medium text-secondary-700 mb-3">
                    Billing Address
                  </h3>
                  <div className="space-y-3">
                    <Input
                      label="Street Address"
                      name="billing_street_address"
                      value={editForm.billing_street_address}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          billing_street_address: e.target.value,
                        })
                      }
                      disabled={isSaving}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label="Unit/Suite"
                        name="billing_unit"
                        value={editForm.billing_unit}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billing_unit: e.target.value,
                          })
                        }
                        disabled={isSaving}
                      />
                      <Input
                        label="City"
                        name="billing_city"
                        value={editForm.billing_city}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billing_city: e.target.value,
                          })
                        }
                        disabled={isSaving}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <Input
                        label="Province"
                        name="billing_province"
                        value={editForm.billing_province}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billing_province: e.target.value,
                          })
                        }
                        disabled={isSaving}
                      />
                      <Input
                        label="Postal Code"
                        name="billing_postal_code"
                        value={editForm.billing_postal_code}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billing_postal_code: e.target.value,
                          })
                        }
                        disabled={isSaving}
                      />
                      <Input
                        label="Country"
                        name="billing_country"
                        value={editForm.billing_country}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            billing_country: e.target.value,
                          })
                        }
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditing(false)}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveEdit}
                    isLoading={isSaving}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {customer.email && (
                  <div>
                    <span className="text-sm text-secondary-500">Email</span>
                    <p className="text-secondary-900">
                      <a
                        href={`mailto:${customer.email}`}
                        className="text-primary-600 hover:underline"
                      >
                        {customer.email}
                      </a>
                    </p>
                  </div>
                )}

                {customer.billing_street_address && (
                  <div>
                    <span className="text-sm text-secondary-500">
                      Billing Address
                    </span>
                    <p className="text-secondary-900">
                      {customer.billing_unit &&
                        `${customer.billing_unit} - `}
                      {customer.billing_street_address}
                      <br />
                      {customer.billing_city}, {customer.billing_province}{' '}
                      {customer.billing_postal_code}
                      <br />
                      {customer.billing_country}
                    </p>
                  </div>
                )}

                <div>
                  <span className="text-sm text-secondary-500">Projects</span>
                  <p className="text-secondary-900">
                    {customer.project_count}{' '}
                    {customer.project_count === 1 ? 'project' : 'projects'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Phone Numbers Card */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-secondary-900">
                Phone Numbers
              </h2>
              {!isAddingPhone && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingPhone(true)}
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Phone
                </Button>
              )}
            </div>

            {/* Add Phone Form */}
            {isAddingPhone && (
              <div className="mb-4 p-4 bg-secondary-50 rounded-lg border border-secondary-200">
                <h3 className="text-sm font-medium text-secondary-700 mb-3">
                  Add New Phone
                </h3>
                <PhoneInput
                  phone={newPhone}
                  onChange={setNewPhone}
                  showRemove={false}
                  showPrimaryToggle={true}
                  showAdvanced={true}
                  disabled={isAdding}
                />
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setIsAddingPhone(false);
                      setNewPhone(createDefaultPhoneFormItem(false));
                    }}
                    disabled={isAdding}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddPhone}
                    isLoading={isAdding}
                  >
                    Add Phone
                  </Button>
                </div>
              </div>
            )}

            {/* Edit Phone Form */}
            {editingPhoneId && editPhoneForm && (
              <div className="mb-4 p-4 bg-primary-50 rounded-lg border border-primary-200">
                <h3 className="text-sm font-medium text-secondary-700 mb-3">
                  Edit Phone
                </h3>
                <div className="space-y-3">
                  {/* Phone Number and Type */}
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Input
                        label="Phone Number"
                        name="editPhoneNumber"
                        type="tel"
                        value={editPhoneForm.phoneNumber}
                        onChange={(e) =>
                          setEditPhoneForm({
                            ...editPhoneForm,
                            phoneNumber: e.target.value,
                          })
                        }
                        disabled={isUpdating}
                      />
                    </div>
                    <div className="w-28">
                      <label className="block text-sm font-medium text-secondary-700 mb-1">
                        Type
                      </label>
                      <select
                        value={editPhoneForm.phoneType}
                        onChange={(e) =>
                          setEditPhoneForm({
                            ...editPhoneForm,
                            phoneType: e.target.value as PhoneType,
                          })
                        }
                        className="w-full px-3 py-2 border border-secondary-300 rounded-lg
                                   focus:outline-none focus:ring-2 focus:ring-primary-500
                                   focus:border-transparent text-sm"
                        disabled={isUpdating}
                      >
                        {PHONE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Label */}
                  <Input
                    label="Label (optional)"
                    name="editPhoneLabel"
                    value={editPhoneForm.label}
                    onChange={(e) =>
                      setEditPhoneForm({
                        ...editPhoneForm,
                        label: e.target.value,
                      })
                    }
                    placeholder="e.g., John's cell, Office direct"
                    disabled={isUpdating}
                  />

                  {/* Can SMS */}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editPhoneForm.canSms}
                      onChange={(e) =>
                        setEditPhoneForm({
                          ...editPhoneForm,
                          canSms: e.target.checked,
                        })
                      }
                      className="w-4 h-4 text-primary-600 border-secondary-300
                                 rounded focus:ring-primary-500"
                      disabled={isUpdating}
                    />
                    <span className="text-secondary-700">Can receive SMS</span>
                  </label>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">
                      Notes (optional)
                    </label>
                    <textarea
                      value={editPhoneForm.notes}
                      onChange={(e) =>
                        setEditPhoneForm({
                          ...editPhoneForm,
                          notes: e.target.value,
                        })
                      }
                      placeholder="e.g., Best after 6pm"
                      className="w-full px-3 py-2 border border-secondary-300 rounded-lg
                                 focus:outline-none focus:ring-2 focus:ring-primary-500
                                 focus:border-transparent text-sm resize-none"
                      rows={2}
                      disabled={isUpdating}
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCancelEditPhone}
                    disabled={isUpdating}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleSaveEditPhone}
                    isLoading={isUpdating}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            )}

            {/* Phone List */}
            <CustomerPhoneList
              phones={customer.all_phones || []}
              onSetPrimary={setAsPrimary}
              onEdit={handleStartEditPhone}
              onDelete={handleDeletePhone}
              isUpdating={isUpdating || isDeleting}
            />
          </div>
        </Card>
      </div>
    </div>
    </DashboardLayout>
  );
}
